import { Analytics } from '@analytics';
import TopNav from '@components/Nav/TopNav';
import OnboardingShell from '@components/Onboarding/Shell';
import ProfileStep from '@components/Onboarding/ProfileStep';
import TippingStep, { tipErrors } from '@components/Onboarding/TippingStep';
import {
  buildSteps,
  firstIncompleteStep,
  initialAmounts,
  paymentOptionsFor
} from '@components/Onboarding/flow';
import { saveSelectedTours, useTourSelectionStatus } from '@components/TourSelectionGate';
import TourChecklist from '@components/TourSelectionGate/TourChecklist';
import { ONBOARDING_TIPPING_OPTION_V1 } from '@experimentConstants';
import { CircularProgress } from '@material-ui/core';
import * as R from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { SessionStorageKeys } from '../../utils/constants';
import useActivePaymentPlatform from '../../utils/hooks/payment_platform';
import { useInjectReducer } from '../../utils/injectReducer';
import { useInjectSaga } from '../../utils/injectSaga';
import * as actions from '../TapToTipPage/actions';
import reducer from '../TapToTipPage/reducer';
import saga from '../TapToTipPage/saga';

/**
 * First-login setup: Profile, then Tours (only for a guide who has to pick
 * them), then Tipping, then the completion page.
 *
 * Tours sits in the middle so a new guide meets it as part of setup rather than
 * as a separate full-screen picker after they think they have finished. The
 * standalone picker (TourSelectionGate) still catches anyone who skips setup or
 * predates it, and both save through saveSelectedTours, so picking tours here
 * also stops the picker from asking again.
 */

// Analytics event names predate this redesign; keep them so funnels carry on.
const SAVE_TIMEOUT_MS = 30000;

const STEP_EVENT_NAMES = { profile: 'Profile', tours: 'Tours', tipping: 'Tipping Information' };

const STEP_COPY = {
  profile: {
    title: 'Tell guests who you are',
    description: 'Your photo, name and a line about you appear on your tipping page.'
  },
  tours: {
    title: 'Which tours do you guide?',
    description:
      'Pick the tours you lead so your tips and reviews reach you. You can change this later in your profile.'
  },
  tipping: {
    title: 'Set your suggested tip',
    description: 'Choose your currency and what guests see first when they tip you.'
  }
};

const defaultCurrencyFor = country => {
  switch (`${country || ''}`.toLowerCase()) {
    case 'us':
      return 'usd';
    case 'gb':
      return 'gbp';
    case 'ca':
      return 'cad';
    default:
      return 'eur';
  }
};

const Onboarding = ({
  history,
  user,
  profile,
  updateProfile,
  fetchProfile,
  isLoading,
  isUpdatingProfile,
  connectStripeResponse,
  connectStripe,
  uploadUserProfileImage,
  storeError
}) => {
  useInjectReducer({ key: 'taptotip', reducer });
  useInjectSaga({ key: 'taptotip', saga });

  const tourStatus = useTourSelectionStatus(user);
  const activePaymentPlatform = useActivePaymentPlatform(user.userId, connectStripeResponse);

  const mergedProfile = {
    onboarding: { profile: false, tipping: false, payments: false },
    ...profile,
    currency: profile?.currency || defaultCurrencyFor(profile?.country)
  };

  const [activeKey, setActiveKey] = useState(null);
  const [toursShown, setToursShown] = useState(false);
  const [toursDone, setToursDone] = useState(false);
  // Flags saved this session, so a later save can't send an older copy back
  // before the first save's response has refreshed the profile.
  const [savedFlags, setSavedFlags] = useState({});
  const [profileValues, setProfileValues] = useState(null);
  const [profileErrors, setProfileErrors] = useState({});
  const [currency, setCurrency] = useState(null);
  const [amounts, setAmounts] = useState(null);
  const [tipFieldErrors, setTipFieldErrors] = useState({});
  const [selectedTours, setSelectedTours] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const avatarBeforeUpload = useRef(null);

  const toursRequired = Boolean(tourStatus?.required) && !toursDone;
  const steps = buildSteps(toursShown || toursRequired);
  const tipMode =
    mergedProfile.paymentOptions?.paymentOptionEnabled ||
    Analytics.getExperimentOption(ONBOARDING_TIPPING_OPTION_V1.name) ||
    ONBOARDING_TIPPING_OPTION_V1.values.Bespoke;

  const finish = (skipped = false) => {
    sessionStorage.setItem(SessionStorageKeys.SkipAllSteps, skipped);
    history.push('/onboarding/complete');
  };

  useEffect(() => {
    Analytics.track('viewed onboarding', null, true);
    if (!profile) fetchProfile(user.userId);
    if (user?.profile?.stripeConnect?.stripeClientId || user?.profile?.stripeConnectCustom?.id) {
      connectStripe();
    }
  }, []);

  useEffect(() => {
    // A payout method already connected counts as the (hidden) payments step.
    if (activePaymentPlatform && profile && !profile.onboarding?.payments) {
      updateProfile({
        userId: user.userId,
        quiet: true,
        profile: { ...mergedProfile, onboarding: { ...mergedProfile.onboarding, payments: true } }
      });
    }
  }, [activePaymentPlatform]);

  useEffect(() => {
    if (tourStatus?.required) setToursShown(true);
  }, [tourStatus]);

  // Seed the forms once the profile arrives.
  useEffect(() => {
    if (!profile) return;
    if (!profileValues) {
      setProfileValues({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        description: profile.description || ''
      });
    }
    if (!currency) setCurrency(profile.revolut?.currency || mergedProfile.currency);
    if (!amounts) setAmounts(initialAmounts(profile.paymentOptions));
  }, [profile]);

  // Resume at the first unfinished step, never moving a guide backwards.
  useEffect(() => {
    if (!profile || !tourStatus) return;
    const next = firstIncompleteStep({ ...mergedProfile.onboarding, ...savedFlags }, toursRequired);
    if (!next) {
      if (!activeKey) finish();
      return;
    }
    const order = buildSteps(true).map(step => step.key);
    setActiveKey(prev => (prev && order.indexOf(prev) > order.indexOf(next) ? prev : next));
  }, [profile, tourStatus]);

  useEffect(() => {
    if (uploading && profile?.avatarUrl && profile.avatarUrl !== avatarBeforeUpload.current) {
      setUploading(false);
    }
  }, [profile?.avatarUrl]);

  // A failed upload shows its own error toast; stop waiting on it so Continue
  // comes back straight away.
  useEffect(() => {
    if (uploading && storeError) setUploading(false);
  }, [storeError]);

  useEffect(() => {
    if (!uploading) return undefined;
    const timer = setTimeout(() => setUploading(false), 20000);
    return () => clearTimeout(timer);
  }, [uploading]);

  if (isLoading || !profile || !tourStatus || !activeKey || !profileValues || !amounts) {
    return (
      <>
        <TopNav />
        <div className="flex items-center justify-center w-full bg-slate-50 h-[calc(100%-77px)] md:h-[calc(100%-93px)]">
          <CircularProgress />
        </div>
      </>
    );
  }

  const index = steps.findIndex(step => step.key === activeKey);
  const goTo = key => {
    setError(null);
    setActiveKey(key);
  };
  const goNext = () => (index < steps.length - 1 ? goTo(steps[index + 1].key) : finish());
  const goBack = index > 0 ? () => goTo(steps[index - 1].key) : null;

  // Resolves once the save lands and rejects if it fails, so a step only moves
  // on with its data stored. The two legacy fields are sent with the same
  // defaults the old onboarding used: a profile created without them (most
  // signups) is otherwise refused by older backends.
  const saveFlags = (flag, extra) =>
    new Promise((resolve, reject) => {
      const flags = { ...savedFlags, [flag]: true };
      updateProfile({
        userId: user.userId,
        quiet: true,
        profile: {
          businessStatus: 'individual',
          taxNumber: '',
          ...mergedProfile,
          ...profileValues,
          ...extra,
          onboarding: { ...mergedProfile.onboarding, ...flags }
        },
        onSuccess: () => {
          setSavedFlags(flags);
          resolve();
        },
        onError: reject
      });
      // The saga uses takeLatest, so a later profile save can cancel this one
      // and neither callback would fire; never leave the step spinning.
      setTimeout(() => reject(new Error('Profile save timed out')), SAVE_TIMEOUT_MS);
    });

  const saveFailed = what => `We couldn't save your ${what}. Check your connection and try again.`;

  const track = suffix => Analytics.track(`${STEP_EVENT_NAMES[activeKey]} ${suffix}`, { page: 'Onboarding' });

  const next = async () => {
    if (activeKey === 'profile') {
      const trimmed = R.map(value => `${value || ''}`.trim(), profileValues);
      const errors = {};
      if (!trimmed.firstName) errors.firstName = 'Enter your first name.';
      if (!trimmed.lastName) errors.lastName = 'Enter your last name.';
      setProfileErrors(errors);
      if (Object.keys(errors).length) return;
      setProfileValues(trimmed);
      track('Next Button Selected');
      setBusy(true);
      setError(null);
      try {
        await saveFlags('profile', trimmed);
        setBusy(false);
        goNext();
      } catch (e) {
        setBusy(false);
        setError(saveFailed('profile'));
      }
      return;
    }

    if (activeKey === 'tours') {
      if (selectedTours.size === 0) return;
      setBusy(true);
      setError(null);
      try {
        await saveSelectedTours(user.userId, [...selectedTours]);
        track('Next Button Selected');
        setToursDone(true);
        setBusy(false);
        goNext();
      } catch (e) {
        setBusy(false);
        setError(saveFailed('tours'));
      }
      return;
    }

    const errors = tipErrors(tipMode, amounts);
    setTipFieldErrors(errors);
    if (Object.keys(errors).length) return;
    track('Next Button Selected');
    setBusy(true);
    setError(null);
    try {
      await saveFlags('tipping', {
        currency,
        paymentOptions: paymentOptionsFor(tipMode, amounts, mergedProfile.paymentOptions)
      });
      setBusy(false);
      finish();
    } catch (e) {
      setBusy(false);
      setError(saveFailed('tip settings'));
    }
  };

  const skip =
    activeKey === 'tours'
      ? null
      : () => {
          track('Skipped');
          if (index === steps.length - 1) finish(true);
          else goNext();
        };

  const uploadPhoto = file => {
    avatarBeforeUpload.current = profile.avatarUrl || null;
    setUploading(true);
    uploadUserProfileImage(file);
  };

  const copy = STEP_COPY[activeKey];

  return (
    <>
      <TopNav />
      <OnboardingShell
        steps={steps}
        activeKey={activeKey}
        title={copy.title}
        description={copy.description}
        primaryLabel={activeKey === 'tipping' ? 'Finish setup' : 'Continue'}
        onPrimary={next}
        primaryDisabled={
          (activeKey === 'tours' && selectedTours.size === 0) ||
          // A profile save sent mid-upload carries the old photo, and its
          // response would replace the new one once the upload lands.
          (activeKey === 'profile' && uploading)
        }
        busy={busy || (activeKey === 'tipping' && isUpdatingProfile)}
        onBack={goBack}
        onSkip={skip}
        error={error}
        status={
          activeKey === 'tours'
            ? selectedTours.size === 0
              ? 'No tours selected'
              : `${selectedTours.size} selected`
            : null
        }>
        {activeKey === 'profile' && (
          <ProfileStep
            values={profileValues}
            errors={profileErrors}
            onChange={(field, value) => {
              setProfileValues(prev => ({ ...prev, [field]: value }));
              if (profileErrors[field]) setProfileErrors(prev => ({ ...prev, [field]: null }));
            }}
            avatarUrl={profile.avatarUrl}
            uploading={uploading}
            onUploadPhoto={uploadPhoto}
          />
        )}
        {activeKey === 'tours' && (
          <TourChecklist
            idPrefix="onboarding-tours"
            tours={tourStatus.tours || []}
            selected={selectedTours}
            onChange={setSelectedTours}
          />
        )}
        {activeKey === 'tipping' && (
          <TippingStep
            mode={tipMode}
            currency={currency}
            onCurrency={setCurrency}
            currencyLocked={Boolean(profile.revolut?.currency)}
            amounts={amounts}
            onAmount={(key, value) => {
              setAmounts(prev => ({ ...prev, [key]: value }));
              if (tipFieldErrors[key]) setTipFieldErrors(prev => ({ ...prev, [key]: null }));
            }}
            errors={tipFieldErrors}
          />
        )}
      </OnboardingShell>
    </>
  );
};

const mapStateToProps = state => ({
  profile: state.taptotip?.profile,
  isLoading: state.taptotip?.isLoading,
  isUpdatingProfile: state.taptotip?.isUpdatingProfile,
  connectStripeResponse: state.taptotip?.connectStripeResponse,
  storeError: state.taptotip?.error,
  user: state.user?.user
});

const mapDispatchToProps = dispatch => ({
  fetchProfile: userId => dispatch(actions.fetchProfile(userId)),
  updateProfile: R.compose(
    dispatch,
    actions.updateProfile
  ),
  connectStripe: () => dispatch(actions.connectStripe()),
  uploadUserProfileImage: image => dispatch(actions.uploadUserProfileImage(image))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Onboarding);
